import { createSlice } from '@reduxjs/toolkit'
import { getFromLocalStorage, setToLocalStorage } from '../utils/localStorage'

const initialState = {
  selectedPlanDetails: {},
  claimEligibilityDetails: [],
  selectedSoldPlanCoverageDetails: {},
  pickupAddress: {},
  serviceAvailabilityDetails: {},
  selectedMode: {},
  selectedShippingLocation: {},
  selectedIssues: {},
  isRequestCreationInProcess: false,
  productDetails: {},
  advanceExchangeDetails: {},
  emptyBoxServiceAvailabilityDetails: {},
  selectedReplacementAddress: {},
  replacementOptions: [],
  selectedReplacementOption: {},
  damageType: {},
  requestInitiationPage: '',
  additionalUploadInfo: '',
  trackRequestDetails: {},
}

const requestSlice = createSlice({
  name: 'requestSlice',
  initialState: getFromLocalStorage('requestData') || initialState,
  reducers: {
    setIsRequestCreationInProcess: (state, action) => {
      state.isRequestCreationInProcess = action.payload
    },
    setSelectedPlanDetails: (state, action) => {
      state.selectedPlanDetails = action.payload
    },
    setEligiblePlansNew: (state, action) => {
      state.eligiblePlansNew = action.payload
    },
    setClaimEligibilityDetails: (state, action) => {
      state.claimEligibilityDetails = action.payload
    },
    setSelectedSoldPlanCoverageDetails: (state, action) => {
      state.selectedSoldPlanCoverageDetails = action.payload
    },
    setDamageTypes: (state, action) => {
      state.damageTypes = action.payload
    },
    setClaimFormDetails: (state, action) => {
      state.claimFormDetails = action.payload
    },
    setPickupAddress: (state, action) => {
      const pickupAddress = action.payload && Object.keys(action.payload).length ? { ...state.pickupAddress, ...action.payload } : {}
      state.pickupAddress = pickupAddress
    },
    setDeliveryAddress: (state, action) => {
      const deliveryAddress = action.payload && Object.keys(action.payload).length ? { ...state.deliveryAddress, ...action.payload } : {}
      state.deliveryAddress = deliveryAddress
    },
    setServiceAvailabilityDetails: (state, action) => {
      state.serviceAvailabilityDetails = action.payload
    },
    setSelectedMode: (state, action) => {
      state.selectedMode = action.payload
    },
    setSelectedShippingLocation: (state, action) => {
      state.selectedShippingLocation = action.payload
    },
    setSlotDetails: (state, action) => {
      state.selectedDay = action.payload.selectedDay
      state.selectedSlot = action.payload.selectedSlot
    },
    setSelectedIssues: (state, action) => {
      state.selectedIssues = action.payload
    },
    setAdditionalUploadInfo: (state, action) => {
      state.additionalUploadInfo = action.payload
    },
    setProductDetails: (state, action) => {
      state.productDetails = action.payload
    },
    setAdvanceExchangeDetails: (state, action) => {
      state.advanceExchangeDetails = action.payload
    },
    setEmptyBoxServiceAvailabilityDetails: (state, action) => {
      state.emptyBoxServiceAvailabilityDetails = action.payload
    },
    setReplacementOptions: (state, action) => {
      state.replacementOptions = action.payload
    },
    setSelectedReplacementAddress: (state, action) => {
      state.selectedReplacementAddress = action.payload
    },
    setSelectedReplacementOption: (state, action) => {
      state.selectedReplacementOption = action.payload
    },
    setDamageType: (state, action) => {
      state.damageType = action.payload
    },
    setRequestInitiationPage: (state, action) => {
      state.requestInitiationPage = action.payload
    },
    setTrackRequestDetails: (state, action) => {
      state.trackRequestDetails = action.payload
    },
    resetRequestData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) => action.type.includes('requestSlice'),
        (state) => {
          setToLocalStorage('requestData', state)
        }
      )
      .addMatcher(
        (action) => action.type.includes('/setSelectedSoldPlanCoverageDetails'),
        (state) => {
          const resetStates = {
            claimFormDetails: {},
            serviceAvailabilityDetails: {},
            selectedMode: {},
            selectedIssues: {},
            replacementOptions: {},
            pickupAddress: {},
            emptyBoxServiceAvailabilityDetails: {},
            advanceExchangeDetails: {},
            deliveryAddress: {},
            selectedDay: {},
            selectedSlot: {},
          }
          return { ...state, ...resetStates }
        }
      )
      .addMatcher(
        (action) => action.type.includes('/setClaimFormDetails'),
        (state) => {
          const resetStates = {
            serviceAvailabilityDetails: {},
            selectedMode: {},
            replacementOptions: {},
            pickupAddress: {},
            emptyBoxServiceAvailabilityDetails: {},
            advanceExchangeDetails: {},
            deliveryAddress: {},
            selectedDay: {},
            selectedSlot: {},
            selectedIssues: {},
          }
          return { ...state, ...resetStates }
        }
      )
      .addMatcher(
        (action) => action.type.includes('/setSelectedMode'),
        (state) => {
          const resetStates = {
            replacementOptions: {},
            emptyBoxServiceAvailabilityDetails: {},
            deliveryAddress: {},
            selectedDay: {},
            selectedSlot: {},
            selectedShippingLocation: {},
            additionalUploadInfo: '',
          }
          return { ...state, ...resetStates }
        }
      )
      .addMatcher(
        (action) => action.type.includes('/setPickupAddress') || action.type.includes('/setSelectedShippingLocation'),
        (state) => {
          const resetStates = {
            replacementOptions: {},
            emptyBoxServiceAvailabilityDetails: {},
            selectedDay: {},
            selectedSlot: {},
          }
          return { ...state, ...resetStates }
        }
      )
      .addMatcher(
        (action) => action.type.includes('/setSlotDetails'),
        (state) => {
          const resetStates = {
            replacementOptions: {},
            emptyBoxServiceAvailabilityDetails: {},
            selectedIssues: {},
          }
          return { ...state, ...resetStates }
        }
      )
  },
})

export default requestSlice.reducer

export const {
  setIsRequestCreationInProcess,
  setSelectedPlanDetails,
  setEligiblePlansNew,
  setClaimEligibilityDetails,
  setSelectedSoldPlanCoverageDetails,
  setDamageTypes,
  setClaimFormDetails,
  resetRequestData,
  setDeliveryAddress,
  setPickupAddress,
  setSelectedMode,
  setSelectedShippingLocation,
  setServiceAvailabilityDetails,
  setSlotDetails,
  setSelectedIssues,
  setProductDetails,
  setAdditionalUploadInfo,
  setAdvanceExchangeDetails,
  setEmptyBoxServiceAvailabilityDetails,
  setSelectedReplacementAddress,
  setReplacementOptions,
  setSelectedReplacementOption,
  setDamageType,
  setRequestInitiationPage,
  setTrackRequestDetails,
} = requestSlice.actions
